import { useEffect, useState } from 'react';
import { NewsCardLink } from '@/features/publicsite/NewsCardLink';
import { publicNewsApi } from '@/features/publicsite/news/api/publicNewsApi';
import type { NewsCard } from '@/features/publicsite/types/NewsCard';
import type { RelatedStoriesProps } from '@/features/publicsite/types/news/RelatedStoriesProps';
import { useLanguage } from '@/i18n/LanguageContext';
import { publicNews } from '@/i18n/dictionaries/publicNews';
import { useTranslation } from '@/i18n/useTranslation';
import { needsLatest, pickRelated } from '@/features/publicsite/relatedPicks';

const RELATED_COUNT = 3;

/** A few cards of the public listing, or none if the request fails — this section is optional. */
function fetchCards(filters: Record<string, string>): Promise<NewsCard[]> {
  return publicNewsApi
    .cards(filters)
    .then((r) => r.items)
    .catch(() => []);
}

/**
 * "Știri similare" — three stories under the one being read, in the same 720px column, after a
 * rule: the cards of /stiri without their frame (NewsCardLink's compact variant), so the section
 * reads as the end of the article rather than a second page stacked under it.
 *
 * Renders nothing until there is something to show, and nothing at all when there is not — a
 * heading over an empty row would promise stories the archive does not have.
 */
export function RelatedStories({ slug, category }: RelatedStoriesProps) {
  const { t } = useTranslation(publicNews);
  const { language } = useLanguage();
  const [related, setRelated] = useState<NewsCard[]>([]);

  useEffect(() => {
    let live = true;
    setRelated([]);
    // One more than needed in each: the story being read may be among them. The category first,
    // the latest after it and only if it is needed (needsLatest) — the section is below the story,
    // so the second round trip, when there is one, is not in the reader's way.
    const load = async () => {
      const sameCategory = category
        ? await fetchCards({ size: String(RELATED_COUNT + 1), category })
        : [];
      const latest = needsLatest(slug, sameCategory)
        ? await fetchCards({ size: String(RELATED_COUNT * 2 + 1) })
        : [];
      if (live) setRelated(pickRelated(slug, sameCategory, latest));
    };
    void load();
    return () => {
      live = false;
    };
  }, [slug, category, language]);

  if (related.length === 0) return null;

  return (
    <section className="story-related" aria-labelledby="story-related-heading">
      <h2 id="story-related-heading" className="story-related__heading">
        {t('relatedHeading')}
      </h2>
      <div className="story-related__grid">
        {related.map((item) => (
          <NewsCardLink
            key={item.slug}
            item={item}
            defaultCategory={t('defaultCategory')}
            language={language}
            variant="compact"
          />
        ))}
      </div>
    </section>
  );
}
